"use client";

import { useEffect, useId, useState, type FormEvent } from "react";
import Link from "next/link";
import { CheckIcon } from "@/components/feature-icons";
import {
  buildEnquiryMailto,
  ENQUIRY_RECIPIENTS,
  FORM_FIELDS,
  validateForm,
  type FormKind,
} from "@/lib/form-schema";

type Values = Record<string, string>;

const controlBase =
  "w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm text-brand-navy outline-none transition-colors placeholder:text-on-surface-variant/50 focus:ring-2";

function control(hasError: boolean) {
  return `${controlBase} ${
    hasError
      ? "border-error focus:border-error focus:ring-error/20"
      : "border-outline-variant focus:border-brand-electric focus:ring-brand-electric/20"
  }`;
}

/** Fired on `window` to pre-select a field, e.g. from a "topic" card. */
export const formPrefillEvent = (kind: FormKind) => `kleipdata:form-prefill:${kind}`;

/**
 * A public enquiry form. The site has no backend: a valid submission opens the
 * visitor's mail app with the details prepared and addressed to the KleipData
 * enquiries mailboxes for them to send.
 */
export function PublicForm({
  kind,
  submitLabel,
  success,
  id,
}: {
  kind: FormKind;
  submitLabel: string;
  success: { title: string; body: string };
  id?: string;
}) {
  const uid = useId();
  const fields = FORM_FIELDS[kind];
  const initial: Values = Object.fromEntries(fields.map((f) => [f.name, ""]));

  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [prepared, setPrepared] = useState(false);

  useEffect(() => {
    function onPrefill(event: Event) {
      const detail = (event as CustomEvent<Record<string, string>>).detail ?? {};
      setValues((v) => {
        const next = { ...v };
        for (const field of fields) {
          const incoming = detail[field.name];
          if (typeof incoming !== "string") continue;
          if (field.options && !field.options.includes(incoming)) continue;
          next[field.name] = incoming;
        }
        return next;
      });
    }
    const name = formPrefillEvent(kind);
    window.addEventListener(name, onPrefill);
    return () => window.removeEventListener(name, onPrefill);
  }, [kind, fields]);

  const fid = (name: string) => `${uid}-${name}`;
  const eid = (name: string) => `${uid}-${name}-error`;

  function set(name: string, value: string) {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: "" } : e));
    setPrepared(false);
  }

  function openMailApp() {
    window.location.href = buildEnquiryMailto(kind, values);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const local = validateForm(kind, values);
    if (!local.ok) {
      setErrors(local.fieldErrors);
      setPrepared(false);
      document.getElementById(fid(Object.keys(local.fieldErrors)[0]!))?.focus();
      return;
    }

    openMailApp();
    setPrepared(true);
  }

  return (
    <form id={id} noValidate onSubmit={handleSubmit} className="flex scroll-mt-28 flex-col gap-5">
      <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2">
        {fields.map((field) => {
          const isWide = field.type === "textarea";
          const err = errors[field.name];
          return (
            <div key={field.name} className={isWide ? "sm:col-span-2" : undefined}>
              <label htmlFor={fid(field.name)} className="mb-1.5 block text-sm font-medium text-brand-navy">
                {field.label}
                {!field.required && (
                  <span className="ml-1 font-normal text-on-surface-variant">(optional)</span>
                )}
              </label>

              {field.type === "select" ? (
                <select
                  id={fid(field.name)}
                  name={field.name}
                  required={field.required}
                  value={values[field.name]}
                  onChange={(e) => set(field.name, e.target.value)}
                  aria-invalid={err ? true : undefined}
                  aria-describedby={err ? eid(field.name) : undefined}
                  className={control(!!err)}
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  id={fid(field.name)}
                  name={field.name}
                  required={field.required}
                  rows={5}
                  maxLength={field.maxLength}
                  value={values[field.name]}
                  onChange={(e) => set(field.name, e.target.value)}
                  aria-invalid={err ? true : undefined}
                  aria-describedby={err ? eid(field.name) : undefined}
                  className={`${control(!!err)} resize-y`}
                />
              ) : (
                <input
                  id={fid(field.name)}
                  name={field.name}
                  type={field.type}
                  required={field.required}
                  maxLength={field.maxLength}
                  autoComplete={field.autoComplete}
                  value={values[field.name]}
                  onChange={(e) => set(field.name, e.target.value)}
                  aria-invalid={err ? true : undefined}
                  aria-describedby={err ? eid(field.name) : undefined}
                  className={control(!!err)}
                />
              )}

              {err && (
                <p id={eid(field.name)} role="alert" className="mt-1.5 text-xs font-medium text-error">
                  {err}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-lg bg-brand-navy px-6 py-3 text-label-md font-semibold text-white transition-colors hover:bg-brand-navy-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric"
        >
          {submitLabel}
        </button>
        <p className="text-xs text-on-surface-variant">
          <Link href="/privacy" className="underline underline-offset-2 hover:text-brand-navy">
            See our Privacy Policy
          </Link>
          .
        </p>
      </div>

      {prepared && (
        <div
          role="status"
          className="flex gap-3.5 rounded-xl border border-brand-electric/30 bg-brand-electric/[0.05] p-5"
        >
          <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-electric" />
          <div className="text-sm leading-6 text-brand-navy">
            <p className="font-semibold">{success.title}</p>
            <p className="mt-1 text-on-surface-variant">
              {success.body} If your email app didn&rsquo;t open,{" "}
              <button
                type="button"
                onClick={openMailApp}
                className="font-medium text-brand-electric underline underline-offset-2"
              >
                open it again
              </button>{" "}
              or email{" "}
              <a
                href={`mailto:${ENQUIRY_RECIPIENTS.join(",")}`}
                className="font-medium text-brand-electric underline underline-offset-2"
              >
                {ENQUIRY_RECIPIENTS[0]}
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </form>
  );
}
